import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { GetServerSideProps } from "next";
import NextError from "next/error";
import { format } from "date-fns";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";
import cx from "classnames";
import { AppContext } from "context/app";
import { DiscountsContext } from "context/discounts";
import { Button } from "@pepdirect/ui/button";
import { Divider } from "@pepdirect/ui/divider";
import { Spinner } from "@pepdirect/ui/spinner";
import { Heading } from "@pepdirect/ui/heading";
import { CheckoutPageContainer } from "components/Checkout/PageContainer";
import { Property } from "components/OrderStatus/Property";
import { RenderedAddress } from "components/OrderStatus/Address";
import { Summary } from "components/OrderSummary/Summary";
import { Fulfillments } from "components/OrderSummary/Fulfillments";
import { OrderItem } from "components/OrderStatus/OrderItem";
import { PaymentImage } from "components/Payment/PaymentImage";
import { useSubmittedOrderForOrderConfirmationQuery } from "services/graphql/generated";
import {
  getPriceSummaryFromSubmittedOrder,
  priceItemsNeedSorting,
  sortGatoradePriceItems,
} from "helpers/checkout";
import { formatPhoneNumber } from "helpers/phone";
import { getPaymentImageUrl } from "helpers/payment";
import { colors } from "@pepdirect/ui/variables";
import st from "./orderStatus.module.scss";
import checkoutSt from "pages/[cartId]/[appId]/checkoutpage.module.scss";

const Email = styled.span`
  color: ${({ theme }) => theme.color.primary};
`;

interface StatusPageProps {
  orderId: string | null;
  appId: string | null;
}

const TITLE = "Order Confirmation";

export const getServerSideProps: GetServerSideProps<StatusPageProps> = async ({
  params,
}) => {
  try {
    return {
      props: {
        orderId: params?.orderId as string,
        appId: params?.appId as string,
      },
    };
  } catch (e) {
    return {
      props: {
        orderId: null,
        appId: null,
      },
    };
  }
};

const Status: FC<StatusPageProps> = ({ orderId, appId }) => {
  const { tenant } = useContext(AppContext);
  const { applyDiscounts } = useContext(DiscountsContext);
  const { data, loading } = useSubmittedOrderForOrderConfirmationQuery({
    variables: { id: orderId },
  });
  const [itemCount, setItemCount] = useState(0);

  const { submittedOrder: submission } = data || {};

  useEffect(() => {
    if (submission?.price?.discounts) {
      applyDiscounts(submission?.price?.discounts);
    }
  }, [submission, applyDiscounts]);

  useEffect(() => {
    if (submission?.price.items) {
      const totalItems = submission.price.items.reduce((total, priceItem) => {
        return total + (priceItem.quantity || 0);
      }, 0);

      setItemCount(totalItems);
    }
  }, [submission]);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    suppressErrors: true,
  });

  if (loading) {
    return (
      <div className={st.loading}>
        <Spinner dotSize="25px" />
      </div>
    );
  }

  if (!submission) {
    return (
      <NextError statusCode={404} title="The requested order does not exist" />
    );
  }

  const fallbackUrl = tenant?.fallbackUrl || "";

  const firstName =
    submission?.giftorName ||
    submission?.contactInfo?.shippingAddress?.firstName ||
    "";
  const email = submission?.contactInfo?.email || "";
  const tempBillingInfo = submission.paymentRequest?.isBillingEqualShipping
    ? submission.contactInfo.shippingAddress
    : submission.paymentRequest?.billingAddress;

  const items = priceItemsNeedSorting(submission.price.items, appId)
    ? sortGatoradePriceItems(submission.price.items)
    : submission.price.items;

  return (
    <CheckoutPageContainer title={`${TITLE} #${submission.externalOrderId}`}>
      <div className={checkoutSt.pageContainer}>
        <div className={cx(st.page, checkoutSt.page)} ref={printRef}>
          <div className={cx(st.top, checkoutSt.container)}>
            <Heading size="l" level="h1">
              Thank you for your order{!!firstName && ` ${firstName}`}!
            </Heading>

            <div className={cx(checkoutSt.section, st.topText)}>
              Your order number is{" "}
              <strong>{`#${submission.externalOrderId}`}</strong>. You&apos;ll
              receive an order confirmation email at <Email>{email}</Email>.
              <br />
              Come back to this page for updates on your order status.
            </div>

            <div className={cx(st.buttons, checkoutSt.section)}>
              <Button type="tertiary" onClick={handlePrint}>
                Print
              </Button>
              <Button type="secondary" internalLink={fallbackUrl}>
                Continue shopping
              </Button>
            </div>
          </div>

          <main className={checkoutSt.main}>
            <div className={cx(checkoutSt.container, checkoutSt.left)}>
              <Heading size="m" level="h2">
                Order Details
              </Heading>

              <div className={st.status}>
                <div>Order Placed</div>
                <div>
                  {format(new Date(submission.submittedAt), "MM/dd/yyyy")}
                </div>
              </div>

              <div className={checkoutSt.section}>
                <Fulfillments fulfillments={submission.fulfillments} />
              </div>

              <Property label="Shipping Info">
                {(!!submission.contactInfo.shippingAddress && (
                  <div className={st.orderDetailsSection}>
                    <RenderedAddress
                      address={submission.contactInfo.shippingAddress}
                    />
                    <div className={st.smallFont}>
                      {formatPhoneNumber(submission.contactInfo.phone)}
                    </div>
                  </div>
                )) ||
                  ""}
              </Property>

              {submission.paymentRequest?.paymentMethod === "CREDIT_CARD" && (
                <Property label="Payment Info">
                  <>
                    <div className={cx(st.paymentInfo, st.orderDetailsSection)}>
                      <PaymentImage
                        className={st.cardImg}
                        src={getPaymentImageUrl(
                          submission.paymentInfo?.paymentType || ""
                        )}
                        isHighlighted
                      />
                      <strong className={st.smallFont}>
                        {submission.paymentInfo?.last4}
                      </strong>
                      <span className={st.smallFont}>
                        Expires:{" "}
                        <strong>
                          {submission.paymentInfo?.cardExpirationDate}
                        </strong>
                      </span>
                    </div>
                    {tempBillingInfo?.line1 && (
                      <div className={st.orderDetailsSection}>
                        <RenderedAddress address={tempBillingInfo} />
                      </div>
                    )}
                  </>
                </Property>
              )}

              {submission.paymentRequest?.paymentMethod === "PAYPAL" && (
                <Property label="Payment Information">
                  Paid with Paypal
                </Property>
              )}
            </div>

            <div className={cx(checkoutSt.right, checkoutSt.container)}>
              <div className={checkoutSt.flexTitle}>
                <Heading level="h2" size="m">
                  Your Order
                </Heading>
                <Heading level="h2" size="m" color={colors.gray}>
                  {`${itemCount} Item${itemCount > 1 ? "s" : ""}`}
                </Heading>
              </div>

              <div className={checkoutSt.section}>
                <Summary
                  price={getPriceSummaryFromSubmittedOrder(submission.price)}
                  feeLabel="Regulatory Fees"
                  disablePromoRemoval
                  showAllocation={false}
                />
              </div>

              <div className={st.order}>
                {items.map(
                  (priceItem, i) =>
                    priceItem && (
                      <div
                        key={`order-item-${i}`}
                        data-testid="order-item-laptop"
                      >
                        {!!i && <Divider />}
                        <OrderItem priceItem={priceItem} />
                      </div>
                    )
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </CheckoutPageContainer>
  );
};

export default Status;
